import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Edit, Calendar, Target, TrendingUp, History, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { Goal } from './GoalCard';

interface GoalFieldChange {
  field: string;
  oldValue: string | number | null;
  newValue: string | number | null;
}

interface GoalChange {
  changes: GoalFieldChange[];
  changedBy: string;
  changedByName?: string | null;
  changedAt: string;
}

interface GoalDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goal: Goal | null;
  onEdit?: (goal: Goal) => void;
}

export function GoalDetailsModal({
  open,
  onOpenChange,
  goal,
  onEdit,
}: GoalDetailsModalProps) {
  if (!goal) return null;

  const progressPercentage = (goal.current / goal.target) * 100;

  const getStatusColor = (status: Goal['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500 text-white';
      case 'on-track':
        return 'bg-blue-500 text-white';
      case 'at-risk':
        return 'bg-yellow-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const getFieldLabel = (field: string) => {
    const labels: Record<string, string> = {
      title: 'Title',
      target: 'Target',
      current: 'Current Progress',
      startDate: 'Start Date',
      endDate: 'End Date',
      deadline: 'Deadline',
      reviewDate: 'Review Date',
      status: 'Status',
      notes: 'Notes',
      reviewFeedback: 'Review Feedback',
    };
    return labels[field] || field;
  };

  const formatValue = (value: string | number | null) => {
    if (value === null || value === undefined) return 'Not set';
    if (typeof value === 'string' && value.includes('T')) {
      try {
        return format(new Date(value), 'MMM dd, yyyy');
      } catch {
        return value;
      }
    }
    return value;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <DialogTitle className="text-2xl">{goal.title}</DialogTitle>
              <DialogDescription className="mt-2">
                Goal details and progress tracking
              </DialogDescription>
            </div>
            <Badge className={getStatusColor(goal.status)}>
              {goal.status.replace('-', ' ')}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Progress Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Progress
              </h3>
              {onEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(goal)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit Goal
                </Button>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Current / Target</span>
                <span className="font-medium text-lg">
                  {goal.current} / {goal.target}
                </span>
              </div>
              <Progress value={progressPercentage} className="h-3" />
              <div className="text-right text-sm text-muted-foreground">
                {Math.round(progressPercentage)}% complete
              </div>
            </div>
          </div>

          <Separator />

          {/* Dates Section */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Timeline
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Start Date</p>
                <p className="font-medium">{format(new Date(goal.startDate), 'MMM dd, yyyy')}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">End Date</p>
                <p className="font-medium">{format(new Date(goal.endDate), 'MMM dd, yyyy')}</p>
              </div>
              {goal.deadline && (
                <div>
                  <p className="text-sm text-muted-foreground">Deadline</p>
                  <p className="font-medium">{format(new Date(goal.deadline), 'MMM dd, yyyy')}</p>
                </div>
              )}
              {goal.reviewDate && (
                <div>
                  <p className="text-sm text-muted-foreground">Review Date</p>
                  <p className="font-medium">{format(new Date(goal.reviewDate), 'MMM dd, yyyy')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Notes Section */}
          {goal.notes && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Notes
                </h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {goal.notes}
                </p>
              </div>
            </>
          )}

          {/* Review Feedback Section */}
          {goal.reviewFeedback && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Review Feedback
                </h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {goal.reviewFeedback}
                </p>
              </div>
            </>
          )}

          {/* Change History Section */}
          {goal.changeHistory && goal.changeHistory.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Change History
                </h3>
                <div className="space-y-4 max-h-64 overflow-y-auto">
                  {goal.changeHistory.map((changeEntry: GoalChange, index: number) => (
                    <div
                      key={index}
                      className="p-4 border rounded-lg bg-muted/30 space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(changeEntry.changedAt), 'MMM dd, yyyy HH:mm')}
                        </span>
                        {changeEntry.changedByName && (
                          <span className="text-xs text-muted-foreground">
                            Changed by {changeEntry.changedByName}
                          </span>
                        )}
                      </div>
                      <div className="space-y-2">
                        {changeEntry.changes.map((fieldChange: GoalFieldChange, fieldIndex: number) => (
                          <div key={fieldIndex} className="flex items-start gap-3">
                            <span className="font-medium text-sm min-w-[120px]">
                              {getFieldLabel(fieldChange.field)}:
                            </span>
                            <div className="text-sm text-muted-foreground flex items-center gap-2 flex-1">
                              <span className="line-through">{formatValue(fieldChange.oldValue)}</span>
                              <span>→</span>
                              <span className="text-foreground font-medium">
                                {formatValue(fieldChange.newValue)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Metadata */}
          <Separator />
          <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
            <div>
              <p>Created: {format(new Date(goal.createdAt), 'MMM dd, yyyy HH:mm')}</p>
            </div>
            <div>
              <p>Updated: {format(new Date(goal.updatedAt), 'MMM dd, yyyy HH:mm')}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
